import { IcoImage } from '@components/icons';
import { useState } from 'preact/hooks';
import { RpxResponse, rpx } from 'client/lib/rpx-client';
import { showError } from '@components/app-error';
import { BtnSecondary } from '@components/buttons';
import { AsyncForm } from '@components/async-form';

type SearchPhotosResult = RpxResponse<typeof rpx.aiPageBuilder.searchPhotos>;
type Photo = SearchPhotosResult['items'][number];

function PhotoCard({ photo, onSelect }: { photo: Photo; onSelect: () => void }) {
  return (
    <div
      class="border rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
      onClick={onSelect}
    >
      <div class="overflow-hidden bg-gray-100 h-48">
        <img
          src={photo.url}
          alt={photo.title || ''}
          class="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      <div class="p-3">
        <h3 class="font-medium text-gray-900 truncate">{photo.title || 'Untitled'}</h3>
        <p class="text-sm text-gray-600 mt-1 line-clamp-4 h-24">{photo.caption}</p>
        <div class="flex justify-between items-center mt-2 text-xs text-gray-500">
          <span>
            {photo.width} × {photo.height}
          </span>

          <div class="flex items-center gap-2">
            {/* Remove this after the initial testing phase */}
            {photo.rankingScore && (
              <span class="bg-blue-100 text-blue-800 px-2 py-1 rounded font-medium">
                {(photo.rankingScore * 100).toFixed(1)}%
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function StockPhotoPicker({ onPick }: { onPick: (photo: Photo) => void }) {
  const [searchState, setSearchState] = useState({
    searching: false,
    loadingMore: false,
    results: [] as Photo[],
    lastSearchTerm: '',
    totalHits: 0,
    currentPage: 1,
    hasNextPage: false,
  });
  const itemsPerPage = 18;

  async function searchPhotos(searchTerm: string, loadMore: boolean) {
    const term = searchTerm.trim();
    if (!term || searchState.searching || searchState.loadingMore) {
      return;
    }

    const page = loadMore ? searchState.currentPage + 1 : 1;

    setSearchState((prev) => ({
      ...prev,
      searching: !loadMore,
      loadingMore: loadMore,
    }));

    try {
      const offset = (page - 1) * itemsPerPage;
      const result = await rpx.aiPageBuilder.searchPhotos({
        query: term,
        limit: itemsPerPage,
        offset,
      });

      setSearchState((prev) => ({
        ...prev,
        searching: false,
        loadingMore: false,
        results: loadMore ? [...prev.results, ...result.items] : result.items,
        totalHits: result.totalHits,
        currentPage: page,
        hasNextPage: result.hasNextPage,
        lastSearchTerm: term,
      }));
    } catch (err) {
      showError(err);
    }
  }

  return (
    <div class="flex flex-col h-full">
      <AsyncForm
        class="mb-6"
        onSubmit={(data: { searchTerm: string }) => searchPhotos(data.searchTerm, false)}
      >
        <div class="flex gap-2">
          <input
            type="text"
            class="flex-1 px-4 py-2 border rounded-md"
            placeholder="Search for stock photos..."
            name="searchTerm"
          />
          <button
            type="submit"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            disabled={searchState.searching}
          >
            {searchState.searching ? 'Searching...' : 'Search'}
          </button>
        </div>
      </AsyncForm>
      <div class="flex-1 overflow-auto">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {searchState.results.length === 0 && !searchState.searching && (
            <div class="col-span-full text-center py-12">
              {searchState.lastSearchTerm ? (
                <div>
                  <IcoImage class="w-16 h-16 text-gray-500 mx-auto mb-4" />
                  <p class="text-gray-600 font-medium">No photos found</p>
                  <p class="text-sm text-gray-500 mt-1">Try with a different search term</p>
                </div>
              ) : (
                <div>
                  <IcoImage class="w-16 h-16 text-gray-500 mx-auto mb-4" />
                  <p class="text-gray-600 font-medium">Enter a search term to find stock photos</p>
                </div>
              )}
            </div>
          )}

          {searchState.searching && (
            <div class="col-span-full text-center py-12 flex flex-col items-center justify-center">
              <div class="w-12 h-12 rounded-full border-4 border-t-blue-600 border-blue-200 animate-spin mb-4"></div>
              <p class="text-gray-600 font-medium">Searching for stock photos...</p>
              <p class="text-sm text-gray-500 mt-1">This may take a few moments</p>
            </div>
          )}

          {searchState.results.map((photo) => (
            <PhotoCard
              key={photo.id}
              photo={photo}
              onSelect={() => {
                onPick(photo);
              }}
            />
          ))}
        </div>

        {searchState.results.length > 0 && (
          <div class="flex flex-col gap-4 mt-8">
            <div class="text-center text-sm text-gray-600">
              Showing {searchState.results.length} of {searchState.totalHits} results
            </div>
            {searchState.hasNextPage && (
              <div class="flex justify-center">
                <BtnSecondary
                  isLoading={searchState.loadingMore}
                  onClick={() => searchPhotos(searchState.lastSearchTerm, true)}
                >
                  Load More
                </BtnSecondary>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
